import { useState } from 'react';
import type { CellShape, NoteColors, NoteFigure, NoteId, Pattern } from '../types';
import { FIGURE_LABELS } from '../types';
import { FigureIcon } from './FigureIcon';
import { BeatFigureContextBar } from './BeatFigureContextBar';
import { BeatFigureContextZone } from './BeatFigureContextZone';
import { MeasureChordButton } from './MeasureChordButton';
import { NoteCell } from './NoteCell';
import { globalCellIndex } from '../utils/pattern';
import type { MeasureChordSummary } from '../utils/chordAccompaniment';

interface Props {
  pattern: Pattern;
  colors: NoteColors;
  shape: CellShape;
  activeIndex: number | null;
  selectedNote: NoteId;
  selectedFigure: NoteFigure;
  visibleMeasures?: number[];
  variant?: 'default' | 'melody';
  layout?: 'stack' | 'row';
  readOnly?: boolean;
  onDeleteMeasure?: (measureIndex: number) => void;
  chordMeasureSummaries?: MeasureChordSummary[];
  onOpenChordMeasure?: (measureIndex: number) => void;
  onCellChange: (
    measureIndex: number,
    beatIndex: number,
    cellIndex: number,
    note: NoteId,
  ) => void;
  onApplyFigure: (measureIndex: number, beatIndex: number) => void;
  onApplyFigureWithFigure?: (
    measureIndex: number,
    beatIndex: number,
    figure: NoteFigure,
  ) => void;
}

export function PatternGrid({
  pattern,
  colors,
  shape,
  activeIndex,
  selectedNote,
  selectedFigure,
  visibleMeasures,
  variant = 'default',
  layout = 'stack',
  readOnly = false,
  onDeleteMeasure,
  chordMeasureSummaries,
  onOpenChordMeasure,
  onCellChange,
  onApplyFigure,
  onApplyFigureWithFigure,
}: Props) {
  const isMelody = variant === 'melody';
  const isRow = isMelody && layout === 'row';
  const canDelete = !readOnly && Boolean(onDeleteMeasure) && pattern.measures.length > 1;
  const indices =
    visibleMeasures ?? pattern.measures.map((_, i) => i);

  const [openFigureMenu, setOpenFigureMenu] = useState<{
    measureIndex: number;
    beatIndex: number;
  } | null>(null);

  const closeFigureMenu = () => setOpenFigureMenu(null);

  return (
    <div
      className={[
        'pattern-grid',
        isMelody ? 'pattern-grid--melody' : '',
        isRow ? 'pattern-grid--melody-row' : '',
        readOnly && isMelody ? 'pattern-grid--melody-play' : '',
        readOnly ? 'pattern-grid--readonly' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {indices.map((mi) => {
        const measure = pattern.measures[mi];
        if (!measure) return null;

        return (
          <section
            key={mi}
            className={`measure-block ${isMelody ? 'measure-block--melody' : ''} ${isRow ? 'measure-block--melody-compact' : ''}`}
            aria-label={`Compás ${mi + 1}`}
          >
            {isMelody && (
              <>
                <span className="measure-deco measure-deco--tl" aria-hidden="true">🎵</span>
                <span className="measure-deco measure-deco--tr" aria-hidden="true">✏️</span>
                <span className="measure-deco measure-deco--bl" aria-hidden="true">🖍️</span>
                <span className="measure-deco measure-deco--br" aria-hidden="true">🎶</span>
              </>
            )}
            <div className="measure-label">
              {!readOnly && onOpenChordMeasure && (
                <MeasureChordButton
                  summary={
                    chordMeasureSummaries?.[mi] ?? {
                      hasChords: false,
                      primaryChordId: null,
                      displayName: 'Armonía',
                    }
                  }
                  measureIndex={mi}
                  onClick={() => onOpenChordMeasure(mi)}
                />
              )}
              <span className="measure-label__title">Compás {mi + 1}</span>
              {canDelete && (
                <button
                  type="button"
                  className="measure-block__delete"
                  onClick={() => onDeleteMeasure?.(mi)}
                  aria-label={`Eliminar compás ${mi + 1}`}
                  title="Eliminar compás"
                >
                  ×
                </button>
              )}
            </div>
            <div className="measure-beats">
              {measure.slots.map((slot, bi) => {
                if (!slot.active) return null;

                const spanClass =
                  slot.span > 1 ? `beat-slot--span-${slot.span}` : '';
                const count = slot.cells.length;

                return (
                  <div
                    key={bi}
                    className={`beat-slot ${spanClass} ${isMelody ? 'beat-slot--melody' : ''}`}
                    style={{ flex: slot.span }}
                  >
                    {isMelody && isRow && !readOnly && onApplyFigureWithFigure ? (
                      <BeatFigureContextZone
                        open={
                          openFigureMenu?.measureIndex === mi &&
                          openFigureMenu?.beatIndex === bi
                        }
                        onClose={closeFigureMenu}
                      >
                        <div className="beat-slot__header beat-slot__header--split">
                          <span className="beat-slot__time">Tiempo {bi + 1}</span>
                          <button
                            type="button"
                            className={`beat-slot__figure beat-slot__figure-trigger ${
                              openFigureMenu?.measureIndex === mi &&
                              openFigureMenu?.beatIndex === bi
                                ? 'beat-slot__figure-trigger--open'
                                : ''
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              const isOpen =
                                openFigureMenu?.measureIndex === mi &&
                                openFigureMenu?.beatIndex === bi;
                              setOpenFigureMenu(isOpen ? null : { measureIndex: mi, beatIndex: bi });
                            }}
                            aria-expanded={
                              openFigureMenu?.measureIndex === mi &&
                              openFigureMenu?.beatIndex === bi
                            }
                            aria-haspopup="toolbar"
                            title="Elegir figura para este tiempo"
                          >
                            <FigureIcon
                              figure={slot.figure}
                              className="beat-slot__figure-icon"
                              size={36}
                              color="#1565c0"
                            />
                            {FIGURE_LABELS[slot.figure]}
                          </button>
                        </div>
                        {openFigureMenu?.measureIndex === mi &&
                          openFigureMenu?.beatIndex === bi && (
                            <BeatFigureContextBar
                              slotFigure={slot.figure}
                              selectedFigure={selectedFigure}
                              onPick={(figure) => {
                                onApplyFigureWithFigure(mi, bi, figure);
                                closeFigureMenu();
                              }}
                            />
                          )}
                      </BeatFigureContextZone>
                    ) : (
                      <div className="beat-slot__header-zone">
                        <button
                          type="button"
                          className="beat-slot__header"
                          onClick={() => !readOnly && onApplyFigure(mi, bi)}
                          disabled={readOnly}
                          title={`Aplicar ${FIGURE_LABELS[selectedFigure]} a este tiempo`}
                        >
                          <span className="beat-slot__time">Tiempo {bi + 1}</span>
                          <span className="beat-slot__figure">
                            {isMelody && (
                              <FigureIcon
                                figure={slot.figure}
                                className="beat-slot__figure-icon"
                                size={isRow ? 36 : 22}
                                color="#1565c0"
                              />
                            )}
                            {isRow ? FIGURE_LABELS[slot.figure] : `Figura: ${FIGURE_LABELS[slot.figure]}`}
                          </span>
                        </button>
                      </div>
                    )}
                    <div
                      key={slot.subdivideKey}
                      className={`beat-slot__cells beat-slot__cells--n${count} beat-slot__cells--pop ${isMelody ? 'beat-slot__cells--melody' : ''}`}
                    >
                      {slot.cells.map((cell, ci) => {
                        const gIdx = globalCellIndex(mi, bi, ci, pattern);
                        return (
                          <NoteCell
                            key={ci}
                            note={cell.note}
                            colors={colors}
                            shape={isMelody ? 'square' : shape}
                            active={activeIndex === gIdx}
                            compact={count > 1}
                            variant={variant}
                            disabled={readOnly}
                            onClick={() => !readOnly && onCellChange(mi, bi, ci, selectedNote)}
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
